import { supabase } from '../lib/supabase';

export interface UploadResult {
  url: string;
  path: string;
}

export interface UploadError {
  message: string;
  details?: any;
}

/**
 * Upload an image file to Supabase Storage
 * @param file - The image file to upload
 * @param userId - The user ID (for organizing files)
 * @param folder - Optional folder name within the user's directory
 * @returns Promise with upload result or error
 */
export async function uploadImage(
  file: File,
  userId: string,
  folder: string = 'templates'
): Promise<UploadResult> {
  try {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      throw new Error('File must be an image');
    }

    // Validate file size (5MB limit)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      throw new Error('File size must be less than 5MB');
    }

    // Generate unique filename
    const fileExt = file.name.split('.').pop()?.toLowerCase();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
    const filePath = `${userId}/${folder}/${fileName}`;

    // Upload file to Supabase Storage
    const { data, error } = await supabase.storage
      .from('listing-images')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) {
      console.error('Upload error:', error);
      throw new Error(`Upload failed: ${error.message}`);
    }

    // Get public URL
    const { data: urlData } = supabase.storage
      .from('listing-images')
      .getPublicUrl(filePath);

    if (!urlData.publicUrl) {
      throw new Error('Failed to get public URL');
    }

    return {
      url: urlData.publicUrl,
      path: filePath
    };
  } catch (error) {
    console.error('Error uploading image:', error);
    throw error;
  }
}

/**
 * Delete an image from Supabase Storage
 * @param filePath - The full path to the file in storage
 * @returns Promise that resolves when deletion is complete
 */
export async function deleteImage(filePath: string): Promise<void> {
  try {
    const { error } = await supabase.storage
      .from('listing-images')
      .remove([filePath]);

    if (error) {
      console.error('Delete error:', error);
      throw new Error(`Delete failed: ${error.message}`);
    }
  } catch (error) {
    console.error('Error deleting image:', error);
    throw error;
  }
}

/**
 * Extract the file path from a public URL
 * @param url - The public URL of the image
 * @returns The file path or null if URL is invalid
 */
export function extractFilePathFromUrl(url: string): string | null {
  try {
    // Extract path from Supabase storage URL
    const match = url.match(/\/storage\/v1\/object\/public\/listing-images\/(.+)$/);
    return match ? match[1] : null;
  } catch (error) {
    console.error('Error extracting file path:', error);
    return null;
  }
}

/**
 * Check if an image URL is valid and accessible
 * @param url - The image URL to check
 * @returns Promise that resolves to true if image is accessible
 */
export async function isImageAccessible(url: string): Promise<boolean> {
  try {
    const response = await fetch(url, { method: 'HEAD' });
    return response.ok;
  } catch (error) {
    console.error('Error checking image accessibility:', error);
    return false;
  }
}